import { FC, useState } from "react";

import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../../Context/RateContext";
import { useRateFieldName } from "../../../hooks/useRateFieldName";
import { useRatesExpansion } from "../../../../RatesExpansion/RatesExpansionContext";
import { useTriggerRateOptionHighlight } from "../../../../RateOptionHighlight/RateOptionHighlightContext";
import { SuggestionBanner } from "../../EventRate/RateSettingSuggestions/SuggestionBanner/SuggestionBanner";

import { detectGroupMembership } from "@/common/helpers/detectGroupMembership";
import {
  getFromLocalStorage,
  getRateSettingSuggestionDismissedKey,
  setInLocalStorage,
} from "@/common/helpers/localStorage";
import { useTranslate } from "@/common/hooks/useTranslate";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface GroupMembershipSuggestionProps {
  field: "name" | "description";
}

export const GroupMembershipSuggestion: FC<GroupMembershipSuggestionProps> = ({ field }) => {
  const { t } = useTranslate();
  const { rateId } = useRateContext();
  const { control, setValue, getValues } = useFormContext<StepTicketsFormData>();
  const { setExpandedRateId } = useRatesExpansion();
  const highlight = useTriggerRateOptionHighlight();
  const name = useWatch({ control, name: useRateFieldName("name") });
  const description = useWatch({ control, name: useRateFieldName("description") });
  const enabledField = useRateFieldName("isGroupMembership");
  const countField = useRateFieldName("groupMembershipMaxMembers");
  const maximumField = useRateFieldName("rateMaximumNumberToBuy");
  const enabled = useWatch({ control, name: enabledField });
  const storageKey = getRateSettingSuggestionDismissedKey(rateId, "group");
  const [dismissed, setDismissed] = useState(() => String(getFromLocalStorage?.(storageKey, "")) === "true");
  const match = detectGroupMembership(name ?? "", description ?? "");

  if (enabled || dismissed || !match || match.field !== field) {
    return null;
  }

  const dismiss = () => {
    setInLocalStorage?.(storageKey, true);
    setDismissed(true);
  };

  return (
    <SuggestionBanner
      outlineDismiss
      lead={t("dashboard", `ticketing.groupMembership.suggestion.${match.audience}.lead`)}
      description={t("dashboard", `ticketing.groupMembership.suggestion.${match.audience}.description`)}
      acceptLabel={t("dashboard", "ticketing.groupMembership.suggestion.accept")}
      dismissLabel={t("dashboard", "ticketing.groupMembership.suggestion.dismiss")}
      onDismiss={dismiss}
      onAccept={() => {
        dismiss();
        setValue(enabledField, true, { shouldDirty: true });
        if (getValues(maximumField) <= 1) {
          setValue(maximumField, 10, { shouldDirty: true });
        }
        setValue(countField, getValues(countField) || 2, { shouldDirty: true });
        setExpandedRateId(rateId);
        highlight(rateId, "groupTicket");
      }}
    />
  );
};
